import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, HealthRing } from "@/components/widgets";
import { healthTone } from "@/lib/helpers";
import { Skeleton } from "@/components/ui/skeleton";
import { Heart, Gauge, Wrench, FileText, Disc, Battery, CheckCircle2, AlertCircle } from "lucide-react";

export default function Health() {
  const [dash, setDash] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setDash(r.data)).catch(() => {});
    api.get("/maintenance").then((r) => setTasks(r.data)).catch(() => {});
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
  }, []);

  if (!dash) return <Skeleton className="h-64 w-full rounded-3xl" />;

  const t = healthTone(dash.health_score);
  const overdueDocs = docs.filter((d) => d.days_left != null && d.days_left < 14).length;
  const pendingTasks = tasks.filter((x) => !x.completed).length;
  const v = dash.vehicle;

  const factors = [
    { label: "Mileage", icon: Gauge, ok: !v || v.mileage < 150000, note: v ? `${v.mileage.toLocaleString()} km` : "—" },
    { label: "Maintenance", icon: Wrench, ok: pendingTasks < 3, note: `${pendingTasks} tasks pending` },
    { label: "Documents", icon: FileText, ok: overdueDocs === 0, note: overdueDocs ? `${overdueDocs} expiring` : "All valid" },
    { label: "Tyres", icon: Disc, ok: true, note: "Log a scan to assess" },
    { label: "Battery", icon: Battery, ok: true, note: "Check due soon" },
  ];

  return (
    <div className="space-y-6" data-testid="health-page">
      <PageHeader title="Vehicle Health" subtitle="How your car dey do." icon={Heart} />

      <div className="rounded-3xl border border-zinc-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-6">
          <HealthRing score={dash.health_score} size={140} />
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-zinc-400">Overall</div>
            <div className={`font-display text-2xl font-bold ${t.tone}`}>{t.label}</div>
            <p className="mt-1 text-sm text-zinc-600">{t.msg}</p>
          </div>
        </div>
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-semibold">What we check</h2>
        <div className="space-y-2">
          {factors.map((f) => (
            <div key={f.label} className="flex items-center gap-4 rounded-2xl border border-zinc-200 bg-white p-4">
              <div className={`grid h-10 w-10 place-items-center rounded-xl ${f.ok ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                <f.icon size={20} />
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium text-zinc-800">{f.label}</div>
                <div className="text-xs text-zinc-400">{f.note}</div>
              </div>
              {f.ok ? <CheckCircle2 className="text-emerald-500" size={20} /> : <AlertCircle className="text-amber-500" size={20} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
